import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Klaviyo's client-side endpoints. Public company id only, no private key in
 * the theme.
 *
 * Deliberately NOT the back-in-stock endpoint. That one attaches the signup to
 * a catalog variant, and this account's Klaviyo catalog is empty, so it has
 * nothing to attach to. It also only fires once stock is already public, which
 * is the opposite of the "first access" this block promises.
 */
const KLAVIYO_SUBSCRIBE = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_EVENTS = 'https://a.klaviyo.com/client/events/';
const KLAVIYO_REVISION = '2025-01-15';

/** Deliberately loose, matching the spec. Anything stricter rejects real addresses. */
const EMAIL = /.+@.+\..+/;

class RestockCapture extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.updateFromVariant);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleClick);
    this.#syncBuyBlock();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.updateFromVariant);
  }

  /**
   * A colour change re-renders only <variant-picker>, so this element pulls its
   * own new markup out of the fetched page. Same approach as product-inventory.
   * @param {CustomEvent} event - The variant update event.
   */
  updateFromVariant = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const next = event.detail?.data?.html?.querySelector('restock-capture');
    if (!next) return;

    morph(this, next, { childrenOnly: true });

    /* childrenOnly leaves the host's own attributes alone, and every one of
       these drives behaviour rather than display, so copy them across. */
    this.dataset.soldOut = next.dataset.soldOut;
    this.dataset.variantId = next.dataset.variantId;
    this.dataset.colorway = next.dataset.colorway;

    this.#syncBuyBlock();
  };

  /**
   * Hide or restore the purchase controls. Not removed from the DOM: the buy
   * block does not re-render on a colour change, so a shopper switching from a
   * sold-out colour back to an available one has to get them back without a
   * page load.
   */
  #syncBuyBlock() {
    const block = this.closest('.buy-buttons-block');
    block?.classList.toggle('buy-buttons-block--sold-out', this.dataset.soldOut === 'true');
  }

  get #email() {
    return /** @type {HTMLInputElement|null} */ (this.querySelector('[data-restock-email]'));
  }

  #valid() {
    return EMAIL.test(this.#email?.value.trim() ?? '');
  }

  #handleInput = () => {
    const submit = this.querySelector('[data-restock-submit]');
    submit?.setAttribute('aria-disabled', this.#valid() ? 'false' : 'true');
  };

  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-restock-reset]')) {
      event.preventDefault();
      this.#showForm();
      return;
    }

    if (!target.closest('[data-restock-submit]')) return;
    event.preventDefault();

    /* Blocked is a no-op, never a silent failure and never a submit. */
    if (!this.#valid()) return;

    this.#submit(this.#email.value.trim());
  };

  async #submit(email) {
    const submit = this.querySelector('[data-restock-submit]');
    const error = this.querySelector('[data-restock-error]');

    submit?.setAttribute('aria-disabled', 'true');
    error?.setAttribute('hidden', '');

    const colorway = this.dataset.colorway || '';
    const details = {
      'Restock Color': colorway,
      'Restock Product': this.dataset.productTitle || '',
      'Restock Variant ID': this.dataset.variantId || '',
      'Restock Product URL': window.location.href,
    };

    try {
      /* 1. Join the list. This is the consent record and the audience. */
      const joined = await fetch(`${KLAVIYO_SUBSCRIBE}?company_id=${this.dataset.companyId}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP Restock Waitlist',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    /* Named "Last ..." on purpose: profile properties are
                       overwritten, so this is only ever the most recent
                       request, never the full history. Segment on the event
                       below instead. */
                    properties: { 'Last Restock Color Requested': colorway },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.listId } } },
          },
        }),
      });

      if (!joined.ok) throw new Error(`Klaviyo subscribe responded ${joined.status}`);

      /* 2. Record WHICH colour was asked for, as its own event.
         One shopper can want Umber today and Bone next month, and a profile
         property cannot hold both - the second overwrites the first. Events
         accumulate, so "asked for Umber at least once" stays true and the
         restock segment for each colour is accurate.

         Best effort: they are already on the list, so a failure here must not
         tell them the signup failed. */
      fetch(`${KLAVIYO_EVENTS}?company_id=${this.dataset.companyId}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: details,
              metric: { data: { type: 'metric', attributes: { name: 'Requested Restock' } } },
              profile: { data: { type: 'profile', attributes: { email } } },
            },
          },
        }),
      }).catch((cause) => console.warn('[restock-capture] event not recorded', cause));

      this.#showConfirmation(email);
    } catch (cause) {
      console.warn('[restock-capture] signup failed', cause);
      if (error) {
        error.textContent = 'That did not go through. Try again in a moment.';
        error.removeAttribute('hidden');
      }
      submit?.setAttribute('aria-disabled', 'false');
    }
  }

  #showConfirmation(email) {
    const colorway = this.dataset.colorway || 'it';
    const panel = this.querySelector('[data-restock-form]');
    if (!panel) return;

    this.dataset.email = email;

    const confirm = document.createElement('div');
    confirm.className = 'restock-capture__confirm';
    confirm.setAttribute('data-restock-confirm', '');

    const heading = document.createElement('h2');
    heading.className = 'restock-capture__heading';
    heading.textContent = 'You are on the list';
    heading.tabIndex = -1;

    const body = document.createElement('p');
    body.className = 'restock-capture__confirm-body';
    /* textContent, not innerHTML: the address is shopper input, and it only
       ever echoes what they actually typed. */
    body.textContent = `We will email ${email} before ${colorway} returns, ahead of the public restock.`;

    const tail = document.createElement('p');
    tail.className = 'restock-capture__confirm-tail';
    tail.textContent = 'Nothing else. One message, when it is back.';

    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'restock-capture__reset';
    reset.setAttribute('data-restock-reset', '');
    reset.textContent = 'Use different details';

    confirm.append(heading, body, tail, reset);
    panel.replaceWith(confirm);

    /* Announce the state change rather than leaving focus on a button that no
       longer exists. */
    heading.focus();
  }

  #showForm() {
    const confirm = this.querySelector('[data-restock-confirm]');
    if (!confirm) return;

    const next = this.#buildForm(this.dataset.email ?? '');
    confirm.replaceWith(next);
    this.#handleInput();
    this.querySelector('[data-restock-email]')?.focus();
  }

  #buildForm(value) {
    const colorway = this.dataset.colorway || 'it';
    const panel = document.createElement('div');
    panel.className = 'restock-capture__panel';
    panel.setAttribute('data-restock-form', '');

    const heading = document.createElement('h2');
    heading.className = 'restock-capture__heading';
    heading.textContent = `Get notified when ${colorway} restocks`;

    const body = document.createElement('p');
    body.className = 'restock-capture__body';
    body.textContent = `We will email you first when ${colorway} is back.`;

    const field = document.createElement('div');
    field.className = 'restock-capture__field';

    const id = `restock-email-${this.dataset.variantId}`;
    const label = document.createElement('label');
    label.className = 'restock-capture__label';
    label.htmlFor = id;
    label.textContent = 'Email';

    const input = document.createElement('input');
    input.className = 'restock-capture__input';
    input.id = id;
    input.type = 'email';
    input.name = 'restock_email';
    input.placeholder = 'you@email.com';
    input.autocomplete = 'email';
    input.setAttribute('inputmode', 'email');
    input.setAttribute('data-restock-email', '');
    input.value = value;

    field.append(label, input);

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'restock-capture__submit';
    submit.setAttribute('aria-disabled', 'true');
    submit.setAttribute('data-restock-submit', '');
    submit.textContent = 'Notify me first';

    const error = document.createElement('p');
    error.className = 'restock-capture__error';
    error.setAttribute('role', 'alert');
    error.setAttribute('hidden', '');
    error.setAttribute('data-restock-error', '');

    panel.append(heading, body, field, submit, error);
    return panel;
  }
}

if (!customElements.get('restock-capture')) {
  customElements.define('restock-capture', RestockCapture);
}
